import { Loader2, PlayCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCreateBulkAnalysis } from "@/hooks/api/use-opportunities";

interface RunAnalysesPopoverProps {
  totalCount: number;
  queryParams: Record<string, string | undefined>;
  isLoading: boolean;
}

type AnalysisType = "milestone" | "direction";

export function RunAnalysesPopover({
  totalCount,
  queryParams,
  isLoading,
}: RunAnalysesPopoverProps) {
  const [open, setOpen] = useState(false);
  const [analysisType, setAnalysisType] = useState<AnalysisType>("milestone");
  const createBulkAnalysis = useCreateBulkAnalysis();

  const isDisabled = totalCount > 100 || isLoading;

  const handleSubmit = async () => {
    // Extract and parse the filters from queryParams
    let parsedFilters = {};
    if (queryParams.filters) {
      try {
        parsedFilters = JSON.parse(queryParams.filters);
      } catch (e) {
        console.error("Failed to parse filters:", e);
      }
    }

    // Prepare the request body with only the structured filters
    const requestBody = {
      analysis_type:
        analysisType === "milestone"
          ? ("opportunity_activation" as const)
          : ("opportunity_direction" as const),
      filters: parsedFilters,
    };

    createBulkAnalysis.mutate(requestBody, {
      onSuccess: (data) => {
        // Show success or error toast based on response
        if (data.errors && data.errors.length > 0) {
          toast.error("Some analyses failed to create", {
            description: `Created: ${data.success_count}, Failed: ${data.failed_count}.`,
          });
        } else {
          toast.success("Analysis tasks submitted successfully.", {
            description: `${data.success_count} ${analysisType} ${
              data.success_count === 1 ? "analysis" : "analyses"
            } created. It will take a few minutes to reflect in the table.`,
          });
        }

        // Close the popover
        setOpen(false);
      },
      onError: (error) => {
        console.error("Error submitting bulk analysis:", error);
        toast.error("Failed to submit analyses", {
          description: error.message || "Unknown error",
        });
      },
    });
  };

  // If disabled, show button with tooltip only (no popover)
  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block">
            <Button
              variant="outline"
              size="sm"
              disabled={true}
              className="h-8 pointer-events-none"
            >
              <PlayCircle className="mr-2 h-4 w-4" />
              Run Analyses
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {totalCount > 100
              ? `Cannot run analyses on more than 100 opportunities (${totalCount} currently match)`
              : "Loading opportunities..."}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // If enabled, show popover with button
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={createBulkAnalysis.isPending}
          className="h-8"
        >
          {createBulkAnalysis.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <PlayCircle className="mr-2 h-4 w-4" />
              Run Analyses
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">Run Analyses</h4>
            <p className="text-sm text-muted-foreground">
              Select the type of analysis to run on {totalCount} matching{" "}
              {totalCount === 1 ? "opportunity" : "opportunities"}.
            </p>
          </div>
          <div className="grid gap-4">
            <RadioGroup
              value={analysisType}
              onValueChange={(value) => setAnalysisType(value as AnalysisType)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="milestone" id="milestone" />
                <Label htmlFor="milestone">
                  <div>
                    <div className="font-medium">
                      Activation Milestone Analysis
                    </div>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="direction" id="direction" />
                <Label htmlFor="direction">
                  <div>
                    <div className="font-medium">Direction Analysis</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={createBulkAnalysis.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={createBulkAnalysis.isPending}
              >
                {createBulkAnalysis.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
